import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../stores';
import {
    getCollectDataCollection,
    postRecipientPreviewTurn,
} from '../../services/api/collectDataService';
import RespondentSidebar, { type ContributorParticipant } from './RespondentSidebar';
import ContributorRightPanel, { type ContributorGuideProgress, type ContributorGuideSection } from './ContributorRightPanel';
import InterviewContextTopBar from '../interview/InterviewContextTopBar';
import { renderInlineBoldMd } from '../../utils/renderInlineBoldMd';
import {
    formatInterviewScrollHeader,
    getChatTimestampHoverTitle,
} from '../../utils/formatDate';
import { contributorInitials } from '../../utils/contributorDisplayName';
import { getTenantInitial, getTenantName } from '../../utils/brandingStyles';
import '../../features/process/process/process-create.css';
import '../../styles/interview-redesign.css';
import { useInterviewThreadScroll } from '../../hooks/useInterviewThreadScroll';

type Msg = { id: string; role: 'user' | 'assistant'; content: string; created_at: string };

type FlatQ = {
    id: string;
    sectionId: string;
    sectionName: string;
    text: string;
    globalIndex: number;
};

function isUserRole(role: string) {
    return role === 'user' || role === 'contributor' || role === 'owner';
}

function flattenGuideQuestions(guide: Record<string, unknown> | null, roleFilter?: string): FlatQ[] {
    if (!guide) return [];

    const norm = (s: string) => (s || '').trim().toLowerCase();
    const matchRole = (label: string) => {
        if (!roleFilter?.trim()) return true;
        const a = norm(label);
        const b = norm(roleFilter);
        return a === b || a.includes(b) || b.includes(a);
    };

    const out: FlatQ[] = [];
    let globalIndex = 0;

    const pushSection = (sec: Record<string, unknown>) => {
        const sectionId = String(sec.id ?? sec._id ?? `sec-${globalIndex}`);
        const sectionName = String(sec.name ?? '').trim() || '—';
        const questions = Array.isArray(sec.questions) ? sec.questions : [];
        const sortedQ = [...questions].sort(
            (q1: Record<string, unknown>, q2: Record<string, unknown>) =>
                (Number(q1.order) || 0) - (Number(q2.order) || 0),
        );
        for (const raw of sortedQ) {
            const q = raw as Record<string, unknown>;
            if (String(q.status ?? '').toLowerCase() === 'removed') continue;
            if (String(q.status ?? '').toLowerCase() !== 'kept') continue;
            const text = String(q.text ?? q.question ?? '').trim();
            const id = String(q.id ?? q._id ?? `q-${globalIndex}`);
            if (!text) continue;
            out.push({ id, sectionId, sectionName, text, globalIndex });
            globalIndex += 1;
        }
    };

    if (Array.isArray(guide.role_sections) && guide.role_sections.length > 0) {
        const roles = [...(guide.role_sections as Array<Record<string, unknown>>)].sort(
            (a, b) => (Number(a.order) || 0) - (Number(b.order) || 0),
        );
        for (const roleBlock of roles) {
            const label = String(roleBlock.role_label ?? '').trim();
            if (!matchRole(label)) continue;
            const sections = Array.isArray(roleBlock.sections) ? roleBlock.sections : [];
            const sorted = [...sections].sort(
                (a, b) => (Number((a as Record<string, unknown>).order) || 0) - (Number((b as Record<string, unknown>).order) || 0),
            );
            for (const sec of sorted) {
                pushSection(sec as Record<string, unknown>);
            }
        }
        return out;
    }

    if (!Array.isArray(guide.sections)) return [];
    const sections = [...guide.sections] as Array<Record<string, unknown>>;
    sections.sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
    for (const sec of sections) {
        pushSection(sec);
    }
    return out;
}

function roleOptionsFromGuide(guide: Record<string, unknown> | null): string[] {
    if (!guide || !Array.isArray(guide.role_sections)) return [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of guide.role_sections) {
        const label = String((raw as Record<string, unknown>).role_label ?? '').trim();
        if (label && !seen.has(label.toLowerCase())) {
            seen.add(label.toLowerCase());
            out.push(label);
        }
    }
    return out;
}

function roleOptionsFromCollection(collection: Record<string, unknown> | null): string[] {
    const tr = collection?.target_roles;
    if (!Array.isArray(tr)) return [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const r of tr) {
        const s = String(r ?? '').trim();
        if (s && !seen.has(s.toLowerCase())) {
            seen.add(s.toLowerCase());
            out.push(s);
        }
    }
    return out;
}

function buildPreviewGuideProgress(
    flat: FlatQ[],
    answeredQuestionIds: string[],
    previewDone: boolean,
): ContributorGuideProgress | null {
    if (!flat.length) return null;
    const answeredSet = new Set(answeredQuestionIds);
    const sectionOrder = [...new Set(flat.map((q) => q.sectionId))];
    const sections: ContributorGuideSection[] = [];
    let answered_kept_total = 0;
    const kept_total = flat.length;
    const nextQ = flat.find((q) => !answeredSet.has(q.id));

    for (const secId of sectionOrder) {
        const inSec = flat.filter((q) => q.sectionId === secId);
        const nk = inSec.length;
        const na = inSec.filter((q) => answeredSet.has(q.id)).length;
        answered_kept_total += na;
        let step_status: ContributorGuideSection['step_status'] = 'upcoming';
        if (previewDone || na >= nk) {
            step_status = 'complete';
        } else if (nextQ?.sectionId === secId) {
            step_status = 'current';
        } else if (na > 0) {
            step_status = 'current';
        }
        sections.push({
            id: secId,
            name: inSec[0]?.sectionName || '—',
            kept_question_count: nk,
            answered_count: na,
            step_status,
        });
    }

    const current_question_number =
        previewDone || !nextQ ? 0 : Math.min(answered_kept_total + 1, kept_total);

    return {
        sections,
        answered_kept_total,
        kept_total,
        current_question_number,
        current_section_name: nextQ?.sectionName ?? null,
    };
}

function messagesToTranscript(messages: Msg[]): Array<{ role: string; content: string }> {
    return messages.map((m) => ({
        role: isUserRole(m.role) ? 'user' : 'assistant',
        content: m.content,
    }));
}

/**
 * Prévisualisation « comme destinataire » — appelle l’API sandbox (reformulation IA, sans persistance).
 */
const CollectRecipientPreviewPage: React.FC = () => {
    const { collectionId } = useParams<{ collectionId: string }>();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const auth = useAppSelector((s) => s.auth);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [collection, setCollection] = useState<Record<string, unknown> | null>(null);
    const [guide, setGuide] = useState<Record<string, unknown> | null>(null);
    const [messages, setMessages] = useState<Msg[]>([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const [status, setStatus] = useState<'in_progress' | 'completed'>('in_progress');
    const [answeredQuestionIds, setAnsweredQuestionIds] = useState<string[]>([]);
    const [previewRole, setPreviewRole] = useState('');
    const [exportLoading, setExportLoading] = useState(false);
    const [bootstrapBusy, setBootstrapBusy] = useState(false);
    const lastBootstrappedRoleRef = useRef<string | null>(null);

    const [chatTimeTick, setChatTimeTick] = useState(0);

    const messageTimestamps = useMemo(
        () => messages.map((m) => m.created_at),
        [messages],
    );

    const { threadScrollRef, scrollHeaderDisplay } = useInterviewThreadScroll({
        messageTimestamps,
        scrollDeps: [messages, sending, bootstrapBusy, status],
    });

    const roleOptions = useMemo(() => {
        const fromCollection = roleOptionsFromCollection(collection);
        if (fromCollection.length) return fromCollection;
        return roleOptionsFromGuide(guide);
    }, [collection, guide]);

    const guideHasKeptQuestions = useMemo(
        () => flattenGuideQuestions(guide).length > 0,
        [guide],
    );

    const resolvedRole =
        (previewRole || roleOptions[0] || '').trim() ||
        t('form.collectWorkspace.contributorDefaultRole', 'Contributor');

    const flatQuestions = useMemo(
        () => flattenGuideQuestions(guide, resolvedRole),
        [guide, resolvedRole],
    );

    const chatReferenceNow = useMemo(
        () => new Date(),
        [chatTimeTick, messages, sending],
    );

    useEffect(() => {
        const id = window.setInterval(() => setChatTimeTick((x) => x + 1), 6000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        if (roleOptions.length && !previewRole) {
            setPreviewRole(roleOptions[0]);
        }
    }, [roleOptions, previewRole]);

    useEffect(() => {
        if (!collectionId || !auth.access_token) {
            setLoading(false);
            setError(t('form.collectWorkspace.recipientPreviewLoadError'));
            return;
        }

        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const cRes = await getCollectDataCollection(collectionId, auth.access_token);
                if (cancelled) return;
                if (cRes.status !== 200 || !cRes.body?.data) {
                    setError(
                        typeof cRes.body?.description === 'string'
                            ? cRes.body.description
                            : t('form.collectWorkspace.recipientPreviewLoadError'),
                    );
                    setLoading(false);
                    return;
                }
                const payload = cRes.body.data as {
                    collection?: Record<string, unknown>;
                    guide?: Record<string, unknown> | null;
                };
                setCollection(payload.collection ?? null);
                setGuide(payload.guide ?? null);
            } catch {
                if (!cancelled) setError(t('form.collectWorkspace.recipientPreviewLoadError'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [collectionId, auth.access_token, t]);

    const workspaceHref = collectionId ? `/process/data-collect/${collectionId}` : '/process/data-collect/create';

    const runBootstrap = useCallback(async () => {
        if (!collectionId || !auth.access_token || !flatQuestions.length) return;
        setBootstrapBusy(true);
        setError(null);
        setMessages([]);
        setAnsweredQuestionIds([]);
        setStatus('in_progress');
        lastBootstrappedRoleRef.current = resolvedRole;
        try {
            const res = await postRecipientPreviewTurn(
                collectionId,
                { role: resolvedRole, bootstrap: true },
                auth.access_token,
            );
            if (res.status !== 200 || !res.body?.data) {
                lastBootstrappedRoleRef.current = null;
                setError(
                    typeof res.body?.description === 'string'
                        ? res.body.description
                        : t('form.collectWorkspace.recipientPreviewLoadError'),
                );
                return;
            }
            const data = res.body.data as { assistant_message?: string };
            const text = String(data.assistant_message ?? '').trim();
            if (!text) {
                lastBootstrappedRoleRef.current = null;
                return;
            }
            setMessages([
                {
                    id: 'preview-seed',
                    role: 'assistant',
                    content: text,
                    created_at: new Date().toISOString(),
                },
            ]);
        } catch {
            lastBootstrappedRoleRef.current = null;
            setError(t('form.collectWorkspace.connectionError', 'Connection error'));
        } finally {
            setBootstrapBusy(false);
        }
    }, [collectionId, auth.access_token, flatQuestions.length, resolvedRole, t]);

    useEffect(() => {
        if (loading || !collection || !flatQuestions.length) return;
        if (lastBootstrappedRoleRef.current === resolvedRole && messages.length > 0) return;
        void runBootstrap();
    }, [loading, collection, flatQuestions.length, resolvedRole, runBootstrap, messages.length]);

    const handlePreviewExport = useCallback(async () => {
        setExportLoading(true);
        try {
            const rawName = String(collection?.name ?? '').trim() || 'collection';
            const safeName = rawName.replace(/[/\\?%*:|"<>]/g, '_').slice(0, 120);
            const you = t('common.you', 'You');
            const header = t(
                'form.collectWorkspace.recipientPreviewExportHeader',
                'Preview walkthrough — text export only (not the XLSX server export).',
            );
            const lines: string[] = [header, `Role: ${resolvedRole}`, ''];
            for (const m of messages) {
                const who = isUserRole(m.role) ? you : getTenantName();
                lines.push(`[${who}]`, m.content, '');
            }
            const body = lines.join('\n');
            const blob = new Blob([body], { type: 'text/plain;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Preview_transcript_${safeName}.txt`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } finally {
            setExportLoading(false);
        }
    }, [messages, collection?.name, resolvedRole, t]);

    const guideProgress: ContributorGuideProgress | null = useMemo(() => {
        return buildPreviewGuideProgress(flatQuestions, answeredQuestionIds, status === 'completed');
    }, [flatQuestions, answeredQuestionIds, status]);

    const displayName = String(auth.user?.fullname ?? auth.user?.email ?? '').trim() || t('common.you', 'You');

    const selfParticipant: ContributorParticipant | null = useMemo(
        () => ({
            id: 'preview-self',
            name: displayName,
            initials: contributorInitials({ name: displayName } as { name: string }),
            role: resolvedRole,
            status: status === 'completed' ? 'completed' : 'in_progress',
            isSelf: true,
        }),
        [displayName, resolvedRole, status, t],
    );

    const handleSend = useCallback(async () => {
        if (!input.trim() || sending || status === 'completed' || !flatQuestions.length) return;
        if (!collectionId || !auth.access_token) return;
        const text = input.trim();
        const priorMessages = messages;
        setInput('');
        setSending(true);
        setMessages((m) => [
            ...m,
            {
                id: `u-${Date.now()}`,
                role: 'user',
                content: text,
                created_at: new Date().toISOString(),
            },
        ]);

        try {
            const transcript = messagesToTranscript(priorMessages);
            const res = await postRecipientPreviewTurn(
                collectionId,
                {
                    role: resolvedRole,
                    bootstrap: false,
                    user_message: text,
                    transcript,
                    answered_question_ids: answeredQuestionIds,
                },
                auth.access_token,
            );
            if (res.status !== 200 || !res.body?.data) {
                setError(
                    typeof res.body?.description === 'string'
                        ? res.body.description
                        : t('form.collectWorkspace.connectionError', 'Connection error'),
                );
                setMessages(priorMessages);
                setInput(text);
                setSending(false);
                return;
            }
            const data = res.body.data as {
                assistant_message?: string;
                mark_complete?: boolean;
                recorded_answers?: Array<{ question_id?: string }>;
            };
            const assistantText = String(data.assistant_message ?? '').trim();
            const recorded = Array.isArray(data.recorded_answers) ? data.recorded_answers : [];
            const newAnswered = [...answeredQuestionIds];
            for (const r of recorded) {
                const qid = String(r.question_id ?? '').trim();
                if (qid && !newAnswered.includes(qid)) newAnswered.push(qid);
            }
            setAnsweredQuestionIds(newAnswered);

            if (data.mark_complete) {
                setMessages((m) => [
                    ...m,
                    {
                        id: `a-${Date.now()}`,
                        role: 'assistant',
                        content:
                            assistantText ||
                            t('form.collectWorkspace.recipientPreviewDone', {
                                defaultValue:
                                    "**You've reached the last question in this guide.** When you go live, Famla continues the conversation here with each contributor.",
                            }),
                        created_at: new Date().toISOString(),
                    },
                ]);
                setStatus('completed');
            } else if (assistantText) {
                setMessages((m) => [
                    ...m,
                    {
                        id: `a-${Date.now()}`,
                        role: 'assistant',
                        content: assistantText,
                        created_at: new Date().toISOString(),
                    },
                ]);
            }
        } catch {
            setMessages(priorMessages);
            setInput(text);
            setError(t('form.collectWorkspace.connectionError', 'Connection error'));
        } finally {
            setSending(false);
        }
    }, [
        input,
        sending,
        status,
        flatQuestions.length,
        collectionId,
        auth.access_token,
        messages,
        answeredQuestionIds,
        resolvedRole,
        t,
    ]);

    if (loading) {
        return (
            <div className="pc-wrap collect-workspace-root">
                <div className="interview-shell" style={{ flex: 1, minHeight: 0 }}>
                    <div className="shell d-flex align-items-center justify-content-center" style={{ minHeight: 240 }}>
                        <p className="collect-muted">{t('words.loading', 'Loading…')}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error && !messages.length) {
        return (
            <div className="pc-wrap collect-workspace-root">
                <div className="interview-shell" style={{ flex: 1, minHeight: 0, padding: 24 }}>
                    <div className="collect-alert danger" role="alert">
                        {error}
                    </div>
                    <Link className="btn btn-theme mt-3" to={workspaceHref}>
                        {t('form.collectWorkspace.recipientPreviewBackToWorkspace', 'Back to collection')}
                    </Link>
                </div>
            </div>
        );
    }

    if (!flatQuestions.length) {
        return (
            <div className="pc-wrap collect-workspace-root">
                <div className="interview-shell" style={{ flex: 1, minHeight: 0, padding: 24 }}>
                    <div className="collect-alert warning" role="alert">
                        {guideHasKeptQuestions
                            ? t(
                                  'form.collectWorkspace.recipientPreviewNoQuestionsForRole',
                                  'No kept questions for this role. Choose another role or generate the script for this role.',
                              )
                            : t(
                                  'form.collectWorkspace.recipientPreviewEmptyGuide',
                                  'Generate the interview script with at least one kept question, then you can preview the respondent experience.',
                              )}
                    </div>
                    {guideHasKeptQuestions && roleOptions.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginTop: 12 }}>
                            <label className="collect-muted" style={{ fontSize: '0.82rem', margin: 0 }}>
                                {t('form.collectWorkspace.recipientPreviewSimulatedRole', 'Simulated role')}
                            </label>
                            <select
                                className="form-select form-select-sm"
                                style={{ maxWidth: 280 }}
                                value={previewRole}
                                onChange={(e) => setPreviewRole(e.target.value)}
                            >
                                {roleOptions.map((r) => (
                                    <option key={r} value={r}>
                                        {r}
                                    </option>
                                ))}
                            </select>
                        </div>
                    ) : null}
                    <button type="button" className="btn btn-theme mt-3" onClick={() => navigate(workspaceHref)}>
                        {t('form.collectWorkspace.recipientPreviewBackToWorkspace', 'Back to collection')}
                    </button>
                </div>
            </div>
        );
    }

    const selfInitials = selfParticipant ? contributorInitials(selfParticipant) : '?';

    return (
        <div className="pc-wrap collect-workspace-root">
            <div
                className="collect-recipient-preview-banner"
                role="status"
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                    padding: '10px 16px',
                    background: 'rgba(212, 83, 126, 0.12)',
                    borderBottom: '1px solid rgba(212, 83, 126, 0.35)',
                    fontSize: '0.9rem',
                    lineHeight: 1.45,
                }}
            >
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
                    {roleOptions.length > 0 ? (
                        <>
                            <label className="collect-muted" style={{ fontSize: '0.82rem', margin: '0 10px 0 0' }}>
                                {t('form.collectWorkspace.recipientPreviewSimulatedRole', 'Simulated role')}
                            </label>
                            <select
                                className="form-select form-select-sm"
                                style={{ maxWidth: 320 }}
                                value={previewRole}
                                onChange={(e) => setPreviewRole(e.target.value)}
                                disabled={sending || bootstrapBusy}
                            >
                                {roleOptions.map((r) => (
                                    <option key={r} value={r}>
                                        {r}
                                    </option>
                                ))}
                            </select>
                        </>
                    ) : null}
                </div>
                <button
                    type="button"
                    className="btn btn-sm btn-light text-dark border-0"
                    style={{ flexShrink: 0 }}
                    onClick={() => navigate(workspaceHref)}
                    title={t('form.collectWorkspace.recipientPreviewCloseHint', 'Return to your collection workspace')}
                >
                    {t('words.close', 'Close')}
                </button>
                {error ? (
                    <div className="collect-alert danger small mb-0 w-100 mt-2" role="alert">
                        {error}
                    </div>
                ) : null}
            </div>
            <div className="interview-shell" style={{ flex: 1, minHeight: 0 }}>
                <div className="shell">
                    <RespondentSidebar
                        selfParticipant={selfParticipant}
                        otherParticipants={[]}
                        inviteEmailHasFamlaAccount
                        activeCollectionId={collectionId}
                        activeCollection={
                            collection
                                ? {
                                      name: (collection.name as string | undefined) ?? undefined,
                                      status: (collection.status as string | undefined) ?? undefined,
                                      created_at: (collection.created_at as string | undefined) ?? undefined,
                                      updated_at: (collection.updated_at as string | undefined) ?? undefined,
                                  }
                                : null
                        }
                    />

                    <div className="centre">
                        <div className="c-topbar">
                            <InterviewContextTopBar
                                processTitle={String(collection?.name ?? t('form.dataCollect.processOverview', 'Data collection'))}
                                hideCreateCta
                                extraActions={
                                    <span className="collect-badge draft">
                                        {t('form.collectWorkspace.recipientPreviewBadge', 'Preview')}
                                    </span>
                                }
                            />
                        </div>

                        <div className="thread" ref={threadScrollRef}>
                            {scrollHeaderDisplay ? (
                                <div className="interview-scroll-date-sticky" aria-live="polite">
                                    {scrollHeaderDisplay}
                                </div>
                            ) : null}
                            {bootstrapBusy || (sending && messages.length === 0) ? (
                                        <div className="turn ai-turn" aria-live="polite">
                                            <div className="t-avatar t-ai">{getTenantInitial()}</div>
                                            <div className="t-body">
                                                <div className="t-sender s-ai">{getTenantName()}</div>
                                                <div className="collect-typing-bubble">
                                                    <span className="collect-typing-dot" />
                                                    <span className="collect-typing-dot" />
                                                    <span className="collect-typing-dot" />
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}
                                    {messages.map((m, i) => {
                                        const isUser = isUserRole(m.role);
                                        const av = isUser ? selfInitials : 'F';
                                        const iso = m.created_at;
                                        const timeLabel = formatInterviewScrollHeader(iso);
                                        const timeTitle = getChatTimestampHoverTitle(iso, chatReferenceNow);
                                return (
                                    <div key={m.id || i} data-msg-timestamp={iso || ''}>
                                        <div className={`turn ${isUser ? 'human human-turn' : 'ai-turn'}`}>
                                            <div className={`t-avatar ${isUser ? 't-human' : 't-ai'}`}>{av}</div>
                                            <div className="t-body">
                                                <div className="t-sender">
                                                    {isUser ? (
                                                        <span className="s-human">
                                                            {t('common.you', 'You')}
                                                            {timeLabel ? (
                                                                <span className="t-time" title={timeTitle}>
                                                                    {timeLabel}
                                                                </span>
                                                            ) : null}
                                                        </span>
                                                    ) : (
                                                        <span className="s-ai">
                                                            {getTenantName()}
                                                            {timeLabel ? (
                                                                <span className="t-time" title={timeTitle}>
                                                                    {timeLabel}
                                                                </span>
                                                            ) : null}
                                                        </span>
                                                    )}
                                                </div>
                                                {isUser ? (
                                                    <div className="bubble-wrap">
                                                        <div className="bubble">{renderInlineBoldMd(m.content)}</div>
                                                    </div>
                                                ) : (
                                                    <div className="bubble">{renderInlineBoldMd(m.content)}</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                                    })}

                                    {sending && messages.length > 0 && (
                                        <div className="turn ai-turn" aria-live="polite">
                                            <div className="t-avatar t-ai">{getTenantInitial()}</div>
                                            <div className="t-body">
                                                <div className="t-sender s-ai">{getTenantName()}</div>
                                                <div className="collect-typing-bubble">
                                                    <span className="collect-typing-dot" />
                                                    <span className="collect-typing-dot" />
                                                    <span className="collect-typing-dot" />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                        </div>

                        <div className="interview-composer-dock">
                            <div className="input-bar">
                                <div className="input-inner">
                                        <textarea
                                            className="input-field"
                                            rows={2}
                                            value={input}
                                            onChange={(e) => setInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey && status !== 'completed') {
                                                    e.preventDefault();
                                                    void handleSend();
                                                }
                                            }}
                                            placeholder={
                                                status === 'completed'
                                                    ? t('form.collectWorkspace.completedPlaceholder', 'Conversation completed')
                                                    : t('form.collectWorkspace.typeMessage', 'Type your response here…')
                                            }
                                            disabled={status === 'completed' || sending || bootstrapBusy}
                                        />
                                        <div className="input-actions">
                                            <button
                                                type="button"
                                                className="input-btn send-btn"
                                                onClick={() => void handleSend()}
                                                disabled={status === 'completed' || !input.trim() || sending || bootstrapBusy}
                                                aria-label={t('words.send', 'Send')}
                                                title={t('words.send', 'Send')}
                                            >
                                                <svg viewBox="0 0 16 16" fill="none" aria-hidden>
                                                    <path
                                                        d="M13 8L3 3L5.5 8L3 13L13 8Z"
                                                        stroke="currentColor"
                                                        strokeWidth="1.2"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    <ContributorRightPanel
                        guideProgress={guideProgress}
                        exportLoading={exportLoading}
                        onExport={handlePreviewExport}
                        inviteEmailHasFamlaAccount
                    />
                </div>
            </div>
        </div>
    );
};

export default CollectRecipientPreviewPage;
